import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./Promotion.module.sass";
import Card from "../../../components/Card";
import Form from "../../../components/Form";
import Table from "./Table";
import Panel from "./Panel";
import Icon from "../../../components/Icon";
import ModalPromotion from "../../../components/ModalPromotion";
import Actions from "../../../components/Actions";

// Services
import { getPromotionsByFormation, createPromotion, updatePromotion, deletePromotion } from "../../../services/promotion.service";
import { showSuccess, showError } from "../../../services/toast.service";

const Promotion = ({ formation }) => {
    const [search, setSearch] = useState("");
    const [promotions, setPromotions] = useState([]);
    const [filteredPromotions, setFilteredPromotions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPromotion, setEditingPromotion] = useState(null);
    const [selectedPromotions, setSelectedPromotions] = useState([]);

    const fetchPromotions = async () => {
        if (!formation || !formation.id) return;
        try {
            setLoading(true);
            const promotionsData = await getPromotionsByFormation(formation.id);
            setPromotions(promotionsData);
            setFilteredPromotions(promotionsData);
        } catch (error) {
            console.error('Erreur lors de la récupération des promotions:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (formation) {
            fetchPromotions();
        }
    }, [formation]);

    const handleOpenModal = (promotion = null) => {
        setEditingPromotion(promotion);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setEditingPromotion(null);
        setIsModalOpen(false);
    };

    const handleSavePromotion = async (formData) => {
        if (!formation || !formation.id) {
            showError("Impossible de sauvegarder la promotion sans référence à la formation.");
            return;
        }
        try {
            if (editingPromotion) {
                const updatedPromotion = await updatePromotion(editingPromotion.id, formData);
                setPromotions(promotions.map(p => p.id === editingPromotion.id ? updatedPromotion : p));
                setFilteredPromotions(filteredPromotions.map(p => p.id === editingPromotion.id ? updatedPromotion : p));
                showSuccess('Promotion mise à jour avec succès.');
            } else {
                const dataToCreate = { ...formData, formations_id: formation.id };
                const newPromotion = await createPromotion(dataToCreate);

                setPromotions([...promotions, newPromotion]);
                setFilteredPromotions([...filteredPromotions, newPromotion]);
                showSuccess('Promotion créée avec succès.');
            }
            handleCloseModal();
            fetchPromotions(); // Refresh list
        } catch (error) {
            showError('Erreur lors de la sauvegarde de la promotion.');
            console.error(error);
        }
    };
    
    // Fonction pour dupliquer une promotion
    const handleDuplicatePromotion = async (promotion) => {
        if (!formation || !formation.id) {
            showError("Impossible de dupliquer la promotion sans référence à la formation.");
            return;
        }
        try {
            // Créer une copie des données de la promotion sans l'ID
            const { id, created, updated, ...promotionData } = promotion;
            
            // Modifier le titre pour indiquer qu'il s'agit d'une copie
            const newTitle = `${promotionData.title_promotion} (copie)`;
            const dataToCreate = { 
                ...promotionData, 
                title_promotion: newTitle,
                formations_id: formation.id 
            };
            
            // Créer la nouvelle promotion
            const newPromotion = await createPromotion(dataToCreate);
            
            // Mettre à jour l'état
            setPromotions([...promotions, newPromotion]);
            setFilteredPromotions([...filteredPromotions, newPromotion]);
            showSuccess('Promotion dupliquée avec succès.');
            
            // Rafraîchir la liste
            fetchPromotions();
        } catch (error) {
            showError('Erreur lors de la duplication de la promotion.');
            console.error(error);
        }
    };
    
    // Fonction pour dupliquer plusieurs promotions sélectionnées
    const handleDuplicateSelected = async (selectedIds) => {
        if (!formation || !formation.id || !selectedIds.length) {
            return;
        }
        
        try {
            // Trouver toutes les promotions sélectionnées
            const promotionsToDuplicate = promotions.filter(p => selectedIds.includes(p.id));
            
            // Dupliquer chaque promotion
            for (const promotion of promotionsToDuplicate) {
                // Créer une copie des données de la promotion sans l'ID
                const { id, created, updated, ...promotionData } = promotion;
                
                // Modifier le titre pour indiquer qu'il s'agit d'une copie
                const newTitle = `${promotionData.title_promotion} (copie)`;
                const dataToCreate = { 
                    ...promotionData, 
                    title_promotion: newTitle,
                    formations_id: formation.id 
                };
                
                // Créer la nouvelle promotion
                await createPromotion(dataToCreate);
            }
            
            // Rafraîchir la liste
            fetchPromotions();
            
            return true; // Indiquer que l'opération a réussi
        } catch (error) {
            showError('Erreur lors de la duplication des promotions.');
            console.error(error);
            throw error;
        }
    };

    const handleSelectPromotion = (promotionId, isSelected) => {
        if (isSelected) {
            setSelectedPromotions([...selectedPromotions, promotionId]);
        } else {
            setSelectedPromotions(selectedPromotions.filter(id => id !== promotionId));
        }
    };

    const handleSelectAll = (isSelected) => {
        if (isSelected) {
            const allPromotionIds = filteredPromotions.map(promotion => promotion.id);
            setSelectedPromotions(allPromotionIds);
        } else {
            setSelectedPromotions([]);
        }
    };

    const handleClearSelection = () => {
        setSelectedPromotions([]);
    };

    const handleDeleteSelected = async (selectedIds) => {
        if (!formation || !formation.id || !selectedIds.length) {
            return;
        }
        
        try {
            // Supprimer chaque promotion
            for (const promotionId of selectedIds) {
                await deletePromotion(promotionId);
            }
            
            // Mettre à jour l'état local
            const remainingPromotions = promotions.filter(p => !selectedIds.includes(p.id));
            setPromotions(remainingPromotions);
            setFilteredPromotions(remainingPromotions.filter(promotion => {
                if (!search.trim()) return true;
                const searchLower = search.toLowerCase();
                const title = (promotion.title_promotion || '').toLowerCase();
                return title.includes(searchLower);
            }));
            
            return true;
        } catch (error) {
            console.error('Erreur lors de la suppression des promotions:', error);
            throw error;
        }
    };

    const handleDeletePromotion = async (promotionId) => {
        if (!formation || !formation.id) {
            showError("Impossible de supprimer la promotion sans référence à la formation.");
            return;
        }
        try {
            await deletePromotion(promotionId);

            setPromotions(promotions.filter(p => p.id !== promotionId));
            setFilteredPromotions(filteredPromotions.filter(p => p.id !== promotionId));
            showSuccess('Promotion supprimée avec succès.');
        } catch (error) {
            showError('Erreur lors de la suppression de la promotion.');
            console.error(error);
        }
    };

    // Fonction pour filtrer les promotions en fonction du texte de recherche
    const filterPromotions = (searchText) => {
        if (!searchText.trim()) {
            setFilteredPromotions(promotions);
            return;
        }

        const searchLower = searchText.toLowerCase();
        const filtered = promotions.filter(promotion => {
            const title = (promotion.title_promotion || '').toLowerCase();
            const startDate = promotion.training_start_date ? new Date(promotion.training_start_date).toLocaleDateString() : '';
            const endDate = promotion.training_end_date ? new Date(promotion.training_end_date).toLocaleDateString() : '';

            return title.includes(searchLower) ||
                startDate.includes(searchLower) ||
                endDate.includes(searchLower);
        });

        setFilteredPromotions(filtered);
    };

    // Effet pour filtrer les promotions lorsque la recherche change
    useEffect(() => {
        filterPromotions(search);
    }, [search, promotions]);

    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        filterPromotions(search);
    };

    const handleActionSelect = (selectedAction) => {
        console.log("Action sélectionnée :", selectedAction);
        switch (selectedAction) {
            case "Ajouter une promotion":
                handleOpenModal();
                break;
            case "Dupliquer sélectionnées":
                if (selectedPromotions.length > 0) {
                    handleDuplicateSelected(selectedPromotions);
                } else {
                    showError("Veuillez sélectionner au moins une promotion à dupliquer");
                }
                break;
            case "Supprimer sélectionnées":
                if (selectedPromotions.length > 0) {
                    handleDeleteSelected(selectedPromotions);
                } else {
                    showError("Veuillez sélectionner au moins une promotion à supprimer");
                }
                break;
            case "Tout sélectionner":
                handleSelectAll(true);
                break;
            case "Tout désélectionner":
                handleClearSelection();
                break;
            default:
                console.warn("Action non reconnue :", selectedAction);
                break;
        }
    };

    // Définition des actions pour le composant Actions
    const actions = [
        {
            title: "Ajouter une promotion",
            action: () => handleActionSelect("Ajouter une promotion")
        }
    ];

    return (
        <>
            <Card
                className={styles.card}
                classCardHead={styles.head}
                title="Promotions"
                classTitle={cn("title-blue", styles.title)}
                head={
                    <>
                      <Form
                        className={styles.form}
                        value={search}
                        setValue={setSearch}
                        onSubmit={() => handleSubmit()}
                        placeholder="Rechercher par titre, dates"
                        type="text"
                        name="search"
                        icon="search"
                      />
                      <div className={styles.sorting}>
                        <Actions
                            className={styles.actions}
                            classActionsHead={styles.actionsHead}
                            items={actions}
                            small
                        />
                      </div>
                    </>
                  }
                >
                <div className={styles.wrapper}>
                    {loading ? (
                        <div className={styles.loading}>
                            <div className="spinner-large" />
                        </div>
                    ) : filteredPromotions.length > 0 ? (
                        <Table 
                            items={filteredPromotions} 
                            title="Promotions de la formation" 
                            onEdit={handleOpenModal}
                            onDelete={handleDeletePromotion}
                            onDuplicate={handleDuplicatePromotion}
                            selectedPromotions={selectedPromotions}
                            onSelectPromotion={handleSelectPromotion}
                            onSelectAll={handleSelectAll}
                        />
                    ) : promotions.length > 0 ? (
                        <div className={styles.empty}>Aucune promotion ne correspond à votre recherche</div>
                    ) : (
                        <div className={styles.empty}>Aucune promotion trouvée pour cette formation.</div>
                    )}
                </div>

                {isModalOpen && (
                    <ModalPromotion
                        visible={isModalOpen}
                        onClose={handleCloseModal}
                        onSave={handleSavePromotion}
                        promotion={editingPromotion}
                        formation={formation}
                    />
                )}
            </Card>
            <Panel 
                selectedPromotions={selectedPromotions} 
                onDeleteSelected={handleDeleteSelected} 
                onClearSelection={handleClearSelection}
                onDuplicateSelected={handleDuplicateSelected}
            />
        </>
    );
};

export default Promotion;
